import type { LocaleCode, LocaleManifest } from './types';

/**
 * URL path conventions for locale routing:
 *
 *   `/`                → default locale (no prefix)         e.g. EN catalog
 *   `/work/{id}`       → default locale, work detail        e.g. EN work
 *   `/br`              → pt-br locale, root                 e.g. PT-BR catalog
 *   `/br/work/{id}`    → pt-br locale, work detail
 *
 * The default locale never carries a URL prefix. Non-default locales mount
 * under their `urlPrefix`. The path AFTER the locale prefix is the canonical
 * route shared across locales.
 *
 * These helpers are pure and isomorphic - no `window.*` access; pass the
 * pathname in.
 */

/**
 * Pick the active locale for a pathname. Longest-prefix match wins; defaults
 * to the manifest's `default` (always `urlPrefix: ""`).
 */
export function detectLocale(pathname: string, manifest: LocaleManifest): LocaleCode {
    const sorted = [...manifest.locales].sort(
        (a, b) => b.urlPrefix.length - a.urlPrefix.length,
    );
    for (const loc of sorted) {
        if (loc.urlPrefix === '') continue; // handled by the fallback at the end
        const prefix = '/' + loc.urlPrefix;
        if (pathname === prefix || pathname.startsWith(prefix + '/')) {
            return loc.code;
        }
    }
    return manifest.defaultLocale;
}

/**
 * Strip the locale prefix from a pathname, returning the canonical path
 * (always starts with `/`). `/br/work/x` → `/work/x`, `/work/x` → `/work/x`, `/br` → `/`.
 */
export function stripLocalePrefix(pathname: string, manifest: LocaleManifest): string {
    const locale = detectLocale(pathname, manifest);
    const entry = manifest.locales.find((l) => l.code === locale);
    if (!entry || !entry.urlPrefix) return pathname || '/';
    const prefix = '/' + entry.urlPrefix;
    if (pathname === prefix) return '/';
    if (pathname.startsWith(prefix + '/')) return pathname.slice(prefix.length);
    return pathname || '/';
}

/**
 * Build a fully-prefixed path for a target locale. `/work/x` + `pt-br` → `/br/work/x`.
 * `/` + `pt-br` → `/br`. `/work/x` + `en` → `/work/x`.
 */
export function withLocalePrefix(
    canonicalPath: string,
    targetLocale: LocaleCode,
    manifest: LocaleManifest,
): string {
    const entry = manifest.locales.find((l) => l.code === targetLocale);
    if (!entry) return canonicalPath;
    const path = canonicalPath.startsWith('/') ? canonicalPath : '/' + canonicalPath;
    if (!entry.urlPrefix) return path;
    if (path === '/') return '/' + entry.urlPrefix;
    return '/' + entry.urlPrefix + path;
}

/**
 * Enumerate every locale-prefixed variant of a canonical path. Useful for
 * declaring routes - feed `/work/:id`, get back `['/work/:id', '/br/work/:id', ...]`
 * so all locale routes register.
 */
export function localizedPaths(canonicalPath: string, manifest: LocaleManifest): string[] {
    return manifest.locales.map((loc) => withLocalePrefix(canonicalPath, loc.code, manifest));
}
